import { EmptyFormLabel } from "@/app/components/form/EmptyFormLabel";
import { InfoTooltip } from "@/app/components/form/FormInfo";
import { Button } from "@/app/components/ui/button";
import { Calendar } from "@/app/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "@/app/components/ui/form";
import { Input } from "@/app/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import { capitalizeFirstLetter } from "@/lib/utils";
import { NoSsr } from "@mui/material";
import { CalendarIcon } from "@radix-ui/react-icons";
import { Flex, Text } from "@radix-ui/themes";
import classNames from "classnames";
import { format, isBefore, isAfter, startOfDay } from "date-fns";
import { useEffect, useState } from "react";
import { Control, useWatch } from "react-hook-form";

interface AuctionDateTimeFormData {
  title: string;
  startDate: Date;
  startTime: string;
  startMeridian: "AM" | "PM";
  endDate: Date;
  endTime: string;
  endMeridian: "AM" | "PM";
}

type DateTimeGroupProps = {
  group: "start" | "end";
  control: any;
  calendarSide?: "top" | "bottom" | "left" | "right";
  labelText?: string;
  labelTooltip?: string;
};

export const AuctionDateTimeGroup = ({
  group,
  control,
  calendarSide = "bottom",
  labelText,
  labelTooltip,
}: DateTimeGroupProps) => {
  const [error, setError] = useState<string>("");
  
  // Get the other date field's value using useWatch for reactivity
  const otherDateField = group === "start" ? "endDate" : "startDate";
  const otherDate = useWatch({
    control,
    name: otherDateField,
  });

  return (
    <Flex direction={"column"}>
      <Flex wrap="wrap" align="end" className="space-x-4">
        <FormField
          control={control}
          name={`${group}Date`}
          render={({ field }) => (
            <FormItem className="flex flex-col md:col-span-2">
              <FormLabel className="text-sm">
                <Flex align="center">
                  <span>{labelText ?? `${capitalizeFirstLetter(group)} Date`}</span>
                  {labelTooltip && (
                    <InfoTooltip className="w-60">{labelTooltip}</InfoTooltip>
                  )}
                </Flex>
              </FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <div className="w-[240px] shrink-0">
                      <Button
                        variant={"outline"}
                        className={classNames(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          <NoSsr>
                            <Text className="text-sm">
                              {format(field.value, "PPP")}
                            </Text>
                          </NoSsr>
                        ) : (
                          <span className="text-sm">Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </div>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent
                  className="w-auto p-0 text-xs"
                  align="start"
                  side={calendarSide}
                  sideOffset={4}
                >
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date: Date) => {
                      const today = startOfDay(new Date());
                      
                      // If the other date is not set, only disable past dates
                      if (!otherDate) {
                        return isBefore(date, today);
                      }

                      // For start date: disable dates after end date
                      if (group === "start") {
                        return isBefore(date, today) || isAfter(date, otherDate);
                      }
                      
                      // For end date: disable dates before start date
                      return isBefore(date, today) || isBefore(date, otherDate);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </FormItem>
          )}
        />
        <Flex className="mt-[6px] space-x-4">
          <FormField
            control={control}
            name={`${group}Time`}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <EmptyFormLabel />
                <FormControl>
                  <div className="w-[96px] shrink-0">
                    <Input placeholder="hh:mm" {...field} className="w-full text-sm" />
                  </div>
                </FormControl>
                <ErrorHandler setErrors={setError} />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${group}Meridian`}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <EmptyFormLabel />
                <FormControl>
                  <div className="w-[80px] shrink-0">
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="AM" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM" className="text-sm">
                          AM
                        </SelectItem>
                        <SelectItem value="PM" className="text-sm">
                          PM
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Flex>
      </Flex>
      {group === "start" && (
        <Text className="text-xs text-muted-foreground mt-1">
          All times are in the local timezone.
        </Text>
      )}
      {error && (
        <Text className="self-center text-[0.8rem] font-medium text-destructive pt-1">
          {error}
        </Text>
      )}
    </Flex>
  );
};

function ErrorHandler({
  setErrors,
}: {
  setErrors: React.Dispatch<React.SetStateAction<string>>;
}) {
  const { error } = useFormField();

  useEffect(() => {
    if (error && error.message) setErrors(error.message);
    else if (!error) setErrors("");
  }, [error, setErrors]);

  return null;
}
